import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  PhotoIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  EyeIcon,
  CheckIcon,
  XMarkIcon,
  CameraIcon,
} from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import axios from "../../../api/axios";
import { toast } from "react-hot-toast";
import { ResourceLoading } from "../../utils/ResourceLoading";
import { ResourceError } from "../../utils/ResourceError";
import { PMPagination } from "../../utils/PMPagination";
import { ModuleFilter } from "../../utils/ModuleFilter";
import { DocumentImageOptimized } from "../documents/utils";
import { DocumentUploadSectionV5 } from "./DocumentUploadSectionV5";
import { EditDocumentDialog } from "./editDocumentDialog";
import PdfPlaceholder from "../../../utils/pdf-placeholder.png";
import ImageViewer from "react-simple-image-viewer";
import { TWclassNames } from "../../utils/Div";
import { documentSrc } from "../documents/utils";
import { Card, CardMedia } from "@mui/material";
import { UploaderV4 } from "./Uploaderv4";
import { PMDialog } from "../../utils/PMDialog";
import { PMField } from "../../utils/PMField";
import { CheckCircleIcon } from "@heroicons/react/24/outline";

const EnhancedLibrary = (props) => {
  const { t } = useTranslation();
  const currentUser = useSelector((state) => state.session.currentUser);
  const isAdmin = currentUser?.roles?.some((r) => r.name === "admin");

  const {
    module,
    module_param,
    moduleParam, // Support both naming conventions
    company_id,
    handleDocument,
    link,
    // PMImagePicker props
    name,
    label,
    setFieldValue,
    onChange,
    selected: selectedProp,
    disabled,
    // Dialog mode props
    inline = false, // If true, shows inline preview like PMImagePicker
    dialogOpen: externalDialogOpen,
    onDialogClose,
  } = props;

  // Use moduleParam if provided, otherwise fall back to module_param
  const effectiveModuleParam = moduleParam || module_param;

  // State for selected document (for inline mode)
  const [selectedDocument, setSelectedDocument] = useState(selectedProp || null);
  const [libraryDialogOpen, setLibraryDialogOpen] = useState(false);

  // State for library view
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(!!module);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage] = useState(24);
  const [totalCount, setTotalCount] = useState(0);
  const [editingDocument, setEditingDocument] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [filters, setFilters] = useState({
    query: "",
    statuses: [],
    modules: [],
    imagesOnly: true, // Filter for "kun bilder" - default true
    filterByModule: module ? true : false, // Filter by module - default true if module is provided
  });

  // Upload state
  const [filesToUpload, setFilesToUpload] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadedCount, setUploadedCount] = useState(0);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const fileInputRef = useRef(null);
  const uploadFunctionRef = useRef(null);
  const [resetUploader, setResetUploader] = useState(null);

  // Drag and drop state
  const [isDragging, setIsDragging] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const dropZoneRef = useRef(null);

  const allowedFileTypes =
    props.allowedFileTypes ||
    "image/png, image/jpeg, image/jpg, image/gif, image/svg, image/svg+xml, image/webp, application/pdf";

  // Update selected document when prop changes
  useEffect(() => {
    if (selectedProp) {
      setSelectedDocument(selectedProp);
    }
  }, [selectedProp]);

  // Determine if dialog should be open
  // If externalDialogOpen is provided, use it. Otherwise use internal state.
  // If externalDialogOpen is provided, we're in dialog mode (not inline)
  const isDialogOpen = externalDialogOpen !== undefined ? externalDialogOpen : libraryDialogOpen;
  const isDialogMode = externalDialogOpen !== undefined || (!inline && isDialogOpen);

  // Load documents function - can be called from anywhere
  const loadDocuments = useCallback(async () => {
    // Always load documents when dialog is open, even without module or search query
    setIsLoading(true);
    setError(null);
    try {
      const params = {
        page: page,
        per_page: perPage,
      };

      // Add module if provided AND filterByModule is true (or not set, default behavior)
      if (module && filters.filterByModule !== false) {
        params.module = module;
      }

      // Add module_param if provided AND filterByModule is true
      if (effectiveModuleParam && filters.filterByModule !== false) {
        params.module_param = effectiveModuleParam;
      }

      // Add company_id if provided
      if (company_id) {
        params.company_id = company_id;
      }

      // Add search query if present (allows global search without module)
      const hasSearchQuery = filters.query && filters.query.trim();
      if (hasSearchQuery) {
        params.search = filters.query.trim();
      }

      const response = await axios.get(`/documents`, { params });
      
      const loadedDocuments = response.data.documents || [];
      const apiTotalCount = response.data.totalCount || response.data.total || loadedDocuments.length;

      setDocuments(loadedDocuments);
      setTotalCount(apiTotalCount);
    } catch (err) {
      console.error("Error loading documents:", err);
      setError(err);
      toast.error("Kunne ikke laste dokumenter");
      setDocuments([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [module, effectiveModuleParam, company_id, page, perPage, filters.query, filters.filterByModule]);

  // Reset page to 1 when search query or filterByModule changes
  useEffect(() => {
    if (page !== 1) {
      setPage(1);
    }
  }, [filters.query, filters.filterByModule]);

  // Update filterByModule default when module changes
  useEffect(() => {
    if (module && filters.filterByModule === undefined) {
      setFilters((prev) => ({
        ...prev,
        filterByModule: true,
      }));
    }
  }, [module]);

  // Load documents when component mounts or dependencies change
  useEffect(() => {
    // Always load documents - either when dialog opens or in full mode
    // Check if dialog is open (either external or internal)
    const dialogIsOpen = externalDialogOpen !== undefined ? externalDialogOpen : libraryDialogOpen;
    
    // Load documents if:
    // 1. Dialog is open (for inline mode)
    // 2. Not inline mode (full library mode)
    if (dialogIsOpen || !inline) {
      loadDocuments();
    }
  }, [loadDocuments, externalDialogOpen, libraryDialogOpen, inline]);

  // Also load documents when dialog opens (for external dialog control)
  useEffect(() => {
    if (externalDialogOpen && externalDialogOpen === true) {
      loadDocuments();
    }
  }, [externalDialogOpen, loadDocuments]);

  // Handle page change
  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  // File selection handler
  const handleFileSelection = useCallback((event) => {
    const files = event.target?.files || (event.files ? Array.from(event.files) : []);
    const selectedFiles = Array.from(files);
    
    if (!selectedFiles.length) {
      return;
    }

    // Allow upload without module - module is optional
    const newFiles = selectedFiles.map((file) => ({
      file,
      previewUrl: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
      title: file.name.replace(/\.[^/.]+$/, ""),
      module: module || null,
      moduleParam: effectiveModuleParam || null,
    }));

    setFilesToUpload((prev) => [...prev, ...newFiles]);
    setUploadDialogOpen(true);
    
    // Reset file input so same file can be selected again
    if (event.target && event.target.value) {
      event.target.value = '';
    }
  }, [module, effectiveModuleParam]);

  // Drag and drop handlers
  const handleDragEnter = useCallback((e) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, [disabled]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dropZoneRef.current?.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        handleFileSelection({ target: { files } });
      }
    },
    [handleFileSelection]
  );

  // Paste handler
  useEffect(() => {
    const handlePaste = (e) => {
      if (!isFocused) return;

      const activeElement = document.activeElement;
      if (
        activeElement &&
        (activeElement.tagName === "INPUT" || activeElement.tagName === "TEXTAREA")
      ) {
        return;
      }

      const items = e.clipboardData?.items;
      if (!items) return;

      const files = [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === "file") {
          const file = item.getAsFile();
          if (file) {
            files.push(file);
          }
        }
      }

      if (files.length > 0) {
        e.preventDefault();
        e.stopPropagation();
        handleFileSelection({ target: { files } });
        setUploadDialogOpen(true);
      }
    };

    if (isFocused) {
      window.addEventListener("paste", handlePaste);
      return () => window.removeEventListener("paste", handlePaste);
    }
  }, [isFocused, handleFileSelection]);

  const handleTitleChange = (index, title) => {
    setFilesToUpload((prev) => {
      const updated = [...prev];
      updated[index].title = title;
      return updated;
    });
  };

  const handleDeleteFile = (indexToDelete) => {
    setFilesToUpload((prev) => {
      const updated = prev.filter((_, index) => index !== indexToDelete);
      if (updated.length === 0) {
        setUploadDialogOpen(false);
      }
      return updated;
    });
  };

  const handleUploadComplete = (uploadedFiles) => {
    const newDocuments = [...documents, ...uploadedFiles];
    setDocuments(newDocuments);
    setFilesToUpload([]);
    setIsUploading(false);
    setUploadProgress({});
    setUploadedCount(0);
    setUploadDialogOpen(false);
    loadDocuments();
    toast.success(`${uploadedFiles.length} dokument(er) lastet opp`);
    if (resetUploader) {
      resetUploader();
    }
  };

  const handleProgressChange = (progress) => {
    setUploadProgress((prev) => ({ ...prev, ...progress }));
  };

  const handleFileUploadComplete = () => {
    setUploadedCount((prev) => prev + 1);
  };

  // Handle document upload (for DocumentUploadSectionV5 compatibility)
  const handleDocumentUploaded = useCallback((count) => {
    loadDocuments();
    toast.success(`${count} dokument(er) lastet opp`);
  }, [loadDocuments]);

  // Update form value helper
  const updateFormValue = useCallback((document) => {
    if (setFieldValue && name) {
      setFieldValue(name, document?.id || null);
    }
  }, [setFieldValue, name]);

  // Handle remove document (for inline mode)
  const handleRemoveDocument = useCallback(() => {
    setSelectedDocument(null);
    updateFormValue(null);
    
    if (onChange) {
      onChange({
        target: {
          name: name,
          value: null,
        },
      });
    }
  }, [updateFormValue, onChange, name]);

  // Handle document delete
  const handleDeleteDocument = useCallback(async (document) => {
    if (!isAdmin) {
      toast.error("Kun administratorer kan slette dokumenter");
      return;
    }

    if (!window.confirm("Er du sikker på at du vil slette dette dokumentet?")) {
      return;
    }

    try {
      await axios.delete(`/documents/${document.id}`);
      toast.success("Dokument slettet");
      
      // If deleted document was selected, clear selection
      if (selectedDocument?.id === document.id) {
        handleRemoveDocument();
      }
      
      loadDocuments();
    } catch (err) {
      console.error("Error deleting document:", err);
      toast.error("Kunne ikke slette dokument");
    }
  }, [isAdmin, loadDocuments, selectedDocument, handleRemoveDocument]);

  // Handle document edit
  const handleEditDocument = useCallback((document) => {
    setEditingDocument(document);
    setEditDialogOpen(true);
  }, []);

  const handleDocumentUpdated = useCallback((updatedDocument) => {
    const newDocuments = documents.map((d) =>
      d.id === updatedDocument.id ? updatedDocument : d
    );
    setDocuments(newDocuments);
    
    // Update selected document if it was edited
    if (selectedDocument?.id === updatedDocument.id) {
      setSelectedDocument(updatedDocument);
      updateFormValue(updatedDocument);
    }
    
    setEditDialogOpen(false);
    setEditingDocument(null);
  }, [documents, selectedDocument, updateFormValue]);

  // Handle document selection (for picker mode)
  const handlePickDocument = useCallback((document) => {
    setSelectedDocument(document);
    
    // Update form value if Formik is used
    updateFormValue(document);
    
    // Call handleDocument callback if provided
    if (handleDocument) {
      handleDocument(document);
    }
    
    // Call onChange if provided
    if (onChange) {
      onChange({
        target: {
          name: name,
          value: document.id,
        },
      });
    }
    
    // Close dialog
    if (externalDialogOpen !== undefined) {
      if (onDialogClose) {
        onDialogClose();
      }
    } else {
      setLibraryDialogOpen(false);
    }
    
    toast.success("Bilde valgt");
  }, [handleDocument, onChange, name, externalDialogOpen, onDialogClose, updateFormValue]);


  // Handle open library dialog
  const handleOpenLibrary = useCallback(() => {
    if (disabled) return;
    if (externalDialogOpen !== undefined) {
      // External dialog control
      if (onDialogClose) {
        // Dialog is controlled externally, just trigger load
        loadDocuments();
      }
    } else {
      setLibraryDialogOpen(true);
    }
  }, [disabled, externalDialogOpen, onDialogClose, loadDocuments]);

  // Handle close library dialog
  const handleCloseLibrary = useCallback(() => {
    if (externalDialogOpen !== undefined) {
      if (onDialogClose) {
        onDialogClose();
      }
    } else {
      setLibraryDialogOpen(false);
    }
  }, [externalDialogOpen, onDialogClose]);

  // Image viewer
  const { imageUrls, documentToImageIndex } = useMemo(() => {
    const urls = [];
    const indexMap = {};
    let imageCount = 0;

    documents.forEach((doc, docIndex) => {
      const filename = doc?.file?.filename?.toLowerCase() || "";
      const isPdf = filename.endsWith(".pdf");
      const hasUrl = doc?.file?.url || doc?.file?.thumb?.url;

      if (!isPdf && hasUrl) {
        urls.push(doc?.file?.url || doc?.file?.thumb?.url);
        indexMap[docIndex] = imageCount;
        imageCount++;
      }
    });

    return { imageUrls: urls.filter(Boolean), documentToImageIndex: indexMap };
  }, [documents]);

  const openImageViewer = useCallback((documentIndex) => {
    const imageIndex = documentToImageIndex[documentIndex] ?? 0;
    setCurrentImageIndex(imageIndex);
    setViewerOpen(true);
  }, [documentToImageIndex]);

  const closeImageViewer = () => {
    setViewerOpen(false);
    setCurrentImageIndex(0);
  };

  // Render library content (shared between inline and full mode)
  const renderLibraryContent = () => {
    return (
      <>
        {/* Always visible Dropzone */}
        <div
          ref={dropZoneRef}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          tabIndex={disabled ? -1 : 0}
          className={TWclassNames(
            "mb-6 border-2 border-dashed rounded-lg p-6 transition-all cursor-pointer",
            isDragging
              ? "border-blue-500 bg-blue-50"
              : "border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50",
            disabled ? "cursor-not-allowed opacity-50" : ""
          )}
          onClick={() => !disabled && fileInputRef.current?.click()}
        >
          <div className="flex flex-col items-center justify-center space-y-2 text-gray-500">
            <PhotoIcon className="h-10 w-10" />
            <p className="text-sm font-medium text-center">
              {isDragging ? t("Slipp bildene her") : t("Dra og slipp bilder eller klikk for å velge")}
            </p>
            <p className="text-xs text-gray-400">{t("Eller trykk Ctrl+V for å lime inn")}</p>
            <p className="text-xs text-gray-400">
              {t("Støttede formater: PNG, JPEG, GIF, SVG, WebP, PDF")}
            </p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept={allowedFileTypes}
            multiple
            className="sr-only"
            onChange={handleFileSelection}
          />
        </div>

        {/* Search and Filter */}
        <div className="mb-4">
          <ModuleFilter
            onFiltersChange={setFilters}
            initialFilters={filters}
            searchPlaceholder={t("Søk etter dokumenter...")}
            moduleOptions={[]}
            userOptions={[]}
            projectOptions={[]}
            hideStatusFilter={true}
            customCheckboxButtonFilters={[
              {
                key: "imagesOnly",
                label: t("Kun bilder") || "Kun bilder",
                defaultValue: true,
              },
              ...(module ? [{
                key: "filterByModule",
                label: t("Kun denne modulen") || `Kun ${module}`,
                defaultValue: true,
              }] : []),
            ]}
          />
        </div>

        {/* Documents Grid */}
        {isLoading && documents.length === 0 ? (
          <div className="text-center py-12">
            <ResourceLoading />
          </div>
        ) : documents.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
            <PhotoIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-500">{t("Ingen dokumenter")}</p>
            <p className="text-sm text-gray-400 mt-2">
              {t("Bruk dropzoneen over for å laste opp dokumenter")}
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {documents
                .filter((document) => {
                  // If "imagesOnly" filter is active, filter out PDFs and non-image files
                  if (filters.imagesOnly) {
                    const filename = document?.file?.filename?.toLowerCase() || "";
                    const fileType = document?.file?.type || "";
                    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp'];
                    const isImage = imageExtensions.some(ext => filename.endsWith(ext)) || 
                                   fileType.startsWith('image/');
                    return isImage;
                  }
                  return true; // Show all documents if filter is not active
                })
                .map((document, index) => {
                  const displayTitle = document?.title || document?.file?.filename || t("Uten tittel");
                  const isSelected = selectedDocument?.id === document.id;
                  return (
                  <div
                    key={document.id}
                    className={TWclassNames(
                      "group relative bg-white rounded-lg border transition-all overflow-hidden cursor-pointer",
                      isSelected
                        ? "border-blue-500 shadow-lg ring-2 ring-blue-500"
                        : "border-gray-200 hover:border-blue-400 hover:shadow-md"
                    )}
                    onClick={() => {
                      if (document?.file?.filename?.toLowerCase().endsWith(".pdf")) {
                        window.open(document?.file?.url, "_blank");
                      } else {
                        openImageViewer(index);
                      }
                    }}
                  >
                    <div className="aspect-square">
                      <DocumentImageOptimized
                        pathType="quickview"
                        document={document}
                        fallback={
                          document?.file?.type === "application/pdf" ? PdfPlaceholder : null
                        }
                      />
                    </div>
                    <div className="p-2">
                      <p className="text-xs font-medium text-gray-900 truncate" title={displayTitle}>
                        {displayTitle}
                      </p>
                    </div>
                    {/* Action buttons - always show on hover */}
                    <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                      {/* Select button - show if in picker mode */}
                      {(handleDocument || inline) && (
                <button
                  type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePickDocument(document);
                          }}
                          className={TWclassNames(
                            "rounded-full p-1.5 transition-colors",
                            isSelected
                              ? "bg-green-600 text-white"
                              : "bg-green-500 hover:bg-green-600 text-white"
                          )}
                          title={isSelected ? t("Valgt") : t("Velg bilde")}
                        >
                          <CheckIcon className="h-3 w-3" />
                </button>
                      )}
                      {/* Edit button - show if admin */}
                      {isAdmin && (
                <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditDocument(document);
                          }}
                          className="bg-blue-500 hover:bg-blue-600 text-white rounded-full p-1.5"
                          title={t("Rediger")}
                        >
                          <PencilIcon className="h-3 w-3" />
                </button>
                      )}
                      {/* Delete button - show if admin */}
                      {isAdmin && (
                <button
                  type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteDocument(document);
                          }}
                          className="bg-red-500 hover:bg-red-600 text-white rounded-full p-1.5"
                          title={t("Slett")}
                        >
                          <TrashIcon className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                    {/* Selected indicator */}
                    {isSelected && (
                      <div className="absolute top-2 left-2 bg-green-500 text-white rounded-full p-1">
                        <CheckIcon className="h-3 w-3" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {totalCount > 0 && totalCount >= perPage && (
              <div className="mt-6">
                <PMPagination
                  currentCount={documents.length}
                  totalCount={totalCount}
                  page={page}
                  onPageChange={handlePageChange}
                  perPage={perPage}
                  showCount={true}
                  showPages={true}
                  showNextPrev={true}
                />
              </div>
            )}
          </>
        )}
      </>
    );
  };

  // Inline mode (like PMImagePicker)
  if (inline) {
      return (
      <>
        <div className="shadow sm:overflow-hidden sm:rounded-md">
          <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
            {label && (
              <div>
                <h3 className="text-lg font-medium leading-6 text-gray-900">
                  {label}
                </h3>
              </div>
            )}
            <div className="grid grid-cols-6 gap-6">
              <div className="col-span-6 sm:col-span-6">
                <Card
                  onClick={() => {
                    if (!disabled && selectedDocument) {
                      const imageUrl = documentSrc(selectedDocument, "original");
                      if (imageUrl) {
                        setViewerOpen(true);
                        setCurrentImageIndex(0);
                      }
                    }
                  }}
                  sx={{ 
                    display: "flex", 
                    height: "200px",
                    cursor: selectedDocument && !disabled ? "pointer" : "default"
                  }}
                >
                  <CardMedia
                    sx={{ width: "100%" }}
                    image={selectedDocument ? documentSrc(selectedDocument, "thumb") : ""}
                    title={selectedDocument ? selectedDocument.title : "Image placeholder"}
                  />
                </Card>
              </div>
              <div className="col-span-6 sm:col-span-6">
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleOpenLibrary}
                    disabled={disabled}
                    className="flex-1 cursor-pointer bg-gray-50 px-4 py-4 text-center text-sm font-medium text-gray-500 hover:text-gray-700 sm:rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors border border-gray-300 hover:bg-gray-100"
                  >
                    {selectedDocument ? t("image.change") : t("image.select") || "Velg bilde"}
                  </button>
                  {selectedDocument && !disabled && (
                    <>
                      <button
                        type="button"
                        onClick={() => handleEditDocument(selectedDocument)}
                        className="px-4 py-4 text-center text-sm font-medium text-gray-500 hover:text-gray-700 sm:rounded-lg transition-colors border border-gray-300 hover:bg-gray-100"
                        title={t("Rediger")}
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button
                        type="button"
                        onClick={handleRemoveDocument}
                        className="px-4 py-4 text-center text-sm font-medium text-red-500 hover:text-red-700 sm:rounded-lg transition-colors border border-red-300 hover:bg-red-50"
                        title={t("Fjern bilde")}
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Library dialog — PMDialog: no backdrop close (unlike HeadlessUI Dialog) so the OS file picker
            closing does not dismiss the library before upload. */}
        <PMDialog
          open={isDialogOpen}
          onClose={handleCloseLibrary}
          title="library"
          size="4xl"
          zIndex={3000}
          panelClassName="max-h-[90vh] flex flex-col"
        >
          <div
            className="flex-1 min-h-0 overflow-y-auto"
            style={{ maxHeight: "calc(90vh - 200px)" }}
          >
            {renderLibraryContent()}
          </div>
          <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3 pt-4 border-t flex-shrink-0">
            <button
              type="button"
              className="inline-flex justify-center w-20 rounded-md border border-transparent bg-gray-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
              onClick={handleCloseLibrary}
            >
              {t("Close")}
            </button>
          </div>
        </PMDialog>

        {/* Image Viewer for selected image */}
        {viewerOpen && selectedDocument && (
          <ImageViewer
            src={[documentSrc(selectedDocument, "original")]}
            currentIndex={0}
            disableScroll={false}
            closeOnClickOutside={true}
            onClose={closeImageViewer}
            backgroundStyle={{
              backgroundColor: "rgba(0,0,0,0.9)",
            }}
          />
        )}

        {/* Upload Dialog */}
        {uploadDialogOpen && (
          <PMDialog
            open={uploadDialogOpen}
            onClose={() => {
              setUploadDialogOpen(false);
              setFilesToUpload([]);
            }}
            title={t("Forhåndsvisning og opplasting")}
            size="2xl"
            zIndex={3500}
          >
            {filesToUpload.length > 0 && (
              <>
                {/* File Previews */}
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {filesToUpload.map((file, index) => (
                    <div
                      key={index}
                      className="flex flex-col gap-3 py-4 bg-gray-100 rounded-xl px-4 relative"
                    >
                      <button
                        type="button"
                        onClick={() => handleDeleteFile(index)}
                        className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1.5"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                      <img
                        src={
                          file.file.type === "application/pdf"
                            ? PdfPlaceholder
                            : file.previewUrl
                        }
                        className="h-40 object-cover rounded"
                        alt="Preview"
                      />
                      <PMField
                        name="title"
                        type="text"
                        label={t("Tittel")}
                        value={file.title}
                        onChange={(e) => handleTitleChange(index, e.target.value)}
                      />
                    </div>
                  ))}
                </div>

                {/* Progress Bar */}
                {isUploading && (
                  <div className="mt-4 w-full bg-gray-300 rounded h-4">
                    <div
                      className="h-full bg-blue-500 rounded"
                      style={{
                        width: `${
                          (Object.values(uploadProgress).reduce((acc, val) => acc + val, 0) /
                            (filesToUpload.length * 100)) *
                          100
                        }%`,
                      }}
                    />
                  </div>
                )}

                {/* Upload Status */}
                {isUploading && (
                  <p className="text-center mt-2 text-sm text-gray-700">
                    {t("Opplasting")} {uploadedCount + 1} {t("av")} {filesToUpload.length}
                  </p>
                )}

                {/* Upload Complete */}
                {uploadedCount === filesToUpload.length && isUploading && (
                  <div className="flex justify-center items-center mt-4 text-green-500">
                    <CheckCircleIcon className="h-6 w-6" />
                    <span className="ml-2 text-green-600 font-semibold">{t("Opplasting fullført")}</span>
                  </div>
                )}

                {/* Add More Files Button */}
                {!isUploading && (
                  <button
                    type="button"
                    className="mt-4 flex items-center justify-center w-full bg-blue-500 text-white p-2 rounded hover:bg-blue-600 transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <CameraIcon className="h-5 w-5 mr-2" /> {t("Legg til flere bilder")}
                  </button>
                )}

                {/* Actions */}
                <div className="flex justify-between mt-4">
                  <button
                    className="w-20 bg-gray-600 text-white p-2 rounded"
                    onClick={() => {
                      setUploadDialogOpen(false);
                      setFilesToUpload([]);
                    }}
                  >
                    {t("Steng")}
                  </button>
                  <button
                    disabled={isUploading || !uploadFunctionRef.current}
                    className="w-20 p-2 rounded text-white bg-emerald-600 hover:bg-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    onClick={async () => {
                      if (!isUploading && typeof uploadFunctionRef.current === "function") {
                        try {
                          setIsUploading(true);
                          setUploadedCount(0);
                          setUploadProgress({});
                          await uploadFunctionRef.current();
                        } catch (error) {
                          console.error("Upload error:", error);
                          toast.error("Feil ved opplasting");
                          setIsUploading(false);
                        }
                      }
                    }}
                  >
                    {isUploading ? t("Laster opp...") : t("Last opp")}
                  </button>
                </div>
              </>
            )}
          </PMDialog>
        )}

        {/* Uploader Component */}
        <UploaderV4
          filesToUpload={filesToUpload}
          onUploadStart={(uploadFn) => {
            if (uploadFn && typeof uploadFn === "function") {
              uploadFunctionRef.current = uploadFn;
            }
          }}
          onUploadComplete={handleUploadComplete}
          onProgressChange={handleProgressChange}
          onFileUploadComplete={handleFileUploadComplete}
          onReset={setResetUploader}
        />

        {/* Edit Dialog */}
        {editDialogOpen && editingDocument && (
          <EditDocumentDialog
            open={editDialogOpen}
            onClose={() => {
              setEditDialogOpen(false);
              setEditingDocument(null);
            }}
            document={editingDocument}
            onRefresh={handleDocumentUpdated}
            editPublic={false}
          />
        )}
      </>
    );
  }

  // Dialog mode (when dialogOpen prop is provided but not inline)
  if (externalDialogOpen !== undefined && !inline) {
    return (
      <>
        <PMDialog
          open={isDialogOpen}
          onClose={handleCloseLibrary}
          title="library"
          size="4xl"
          zIndex={3000}
          panelClassName="max-h-[90vh] flex flex-col"
        >
          <div
            className="flex-1 min-h-0 overflow-y-auto"
            style={{ maxHeight: "calc(90vh - 200px)" }}
          >
            {renderLibraryContent()}
          </div>
          <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3 pt-4 border-t flex-shrink-0">
            <button
              type="button"
              className="inline-flex justify-center w-20 rounded-md border border-transparent bg-gray-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
              onClick={handleCloseLibrary}
            >
              {t("Close")}
            </button>
          </div>
        </PMDialog>

        {/* Upload Dialog */}
        {uploadDialogOpen && (
          <PMDialog
            open={uploadDialogOpen}
            onClose={() => {
              setUploadDialogOpen(false);
              setFilesToUpload([]);
            }}
            title={t("Forhåndsvisning og opplasting")}
            size="2xl"
            zIndex={3500}
          >
            {filesToUpload.length > 0 && (
              <>
                {/* File Previews */}
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {filesToUpload.map((file, index) => (
                    <div
                      key={index}
                      className="flex flex-col gap-3 py-4 bg-gray-100 rounded-xl px-4 relative"
                    >
                      <button
                        type="button"
                        onClick={() => handleDeleteFile(index)}
                        className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1.5"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                      <img
                        src={
                          file.file.type === "application/pdf"
                            ? PdfPlaceholder
                            : file.previewUrl
                        }
                        className="h-40 object-cover rounded"
                        alt="Preview"
                      />
                      <PMField
                        name="title"
                        type="text"
                        label={t("Tittel")}
                        value={file.title}
                        onChange={(e) => handleTitleChange(index, e.target.value)}
                      />
                    </div>
                  ))}
                </div>

                {/* Progress Bar */}
                {isUploading && (
                  <div className="mt-4 w-full bg-gray-300 rounded h-4">
                    <div
                      className="h-full bg-blue-500 rounded"
                      style={{
                        width: `${
                          (Object.values(uploadProgress).reduce((acc, val) => acc + val, 0) /
                            (filesToUpload.length * 100)) *
                          100
                        }%`,
                      }}
                    />
                  </div>
                )}

                {/* Upload Status */}
                {isUploading && (
                  <p className="text-center mt-2 text-sm text-gray-700">
                    {t("Opplasting")} {uploadedCount + 1} {t("av")} {filesToUpload.length}
                  </p>
                )}

                {/* Upload Complete */}
                {uploadedCount === filesToUpload.length && isUploading && (
                  <div className="flex justify-center items-center mt-4 text-green-500">
                    <CheckCircleIcon className="h-6 w-6" />
                    <span className="ml-2 text-green-600 font-semibold">{t("Opplasting fullført")}</span>
                  </div>
                )}

                {/* Add More Files Button */}
                {!isUploading && (
                  <button
                    type="button"
                    className="mt-4 flex items-center justify-center w-full bg-blue-500 text-white p-2 rounded hover:bg-blue-600 transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <CameraIcon className="h-5 w-5 mr-2" /> {t("Legg til flere bilder")}
                  </button>
                )}

                {/* Actions */}
                <div className="flex justify-between mt-4">
                  <button
                    className="w-20 bg-gray-600 text-white p-2 rounded"
                    onClick={() => {
                      setUploadDialogOpen(false);
                      setFilesToUpload([]);
                    }}
                  >
                    {t("Steng")}
                  </button>
                  <button
                    disabled={isUploading || !uploadFunctionRef.current}
                    className="w-20 p-2 rounded text-white bg-emerald-600 hover:bg-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    onClick={async () => {
                      if (!isUploading && typeof uploadFunctionRef.current === "function") {
                        try {
                          setIsUploading(true);
                          setUploadedCount(0);
                          setUploadProgress({});
                          await uploadFunctionRef.current();
                        } catch (error) {
                          console.error("Upload error:", error);
                          toast.error("Feil ved opplasting");
                          setIsUploading(false);
                        }
                      }
                    }}
                  >
                    {isUploading ? t("Laster opp...") : t("Last opp")}
                  </button>
                </div>
              </>
            )}
          </PMDialog>
        )}

        {/* Uploader Component */}
        <UploaderV4
          filesToUpload={filesToUpload}
          onUploadStart={(uploadFn) => {
            if (uploadFn && typeof uploadFn === "function") {
              uploadFunctionRef.current = uploadFn;
            }
          }}
          onUploadComplete={handleUploadComplete}
          onProgressChange={handleProgressChange}
          onFileUploadComplete={handleFileUploadComplete}
          onReset={setResetUploader}
        />

        {/* Edit Dialog */}
        {editDialogOpen && editingDocument && (
          <EditDocumentDialog
            open={editDialogOpen}
            onClose={() => {
              setEditDialogOpen(false);
              setEditingDocument(null);
            }}
            document={editingDocument}
            onRefresh={handleDocumentUpdated}
            editPublic={false}
          />
        )}

        {/* Image Viewer */}
        {viewerOpen && imageUrls.length > 0 && currentImageIndex < imageUrls.length && (
          <ImageViewer
            src={imageUrls}
            currentIndex={currentImageIndex}
            disableScroll={false}
            closeOnClickOutside={true}
            onClose={closeImageViewer}
            backgroundStyle={{
              backgroundColor: "rgba(0,0,0,0.9)",
            }}
          />
        )}
      </>
    );
  }

  // Full library mode (default - no dialog)
  if (isLoading && documents.length === 0) {
    return <ResourceLoading />;
  }

  if (error) {
    return <ResourceError />;
  }

  return (
    <div className="w-full space-y-4">
      {renderLibraryContent()}

      {/* Upload Dialog */}
      {uploadDialogOpen && (
        <PMDialog
          open={uploadDialogOpen}
          onClose={() => {
            setUploadDialogOpen(false);
            setFilesToUpload([]);
          }}
          title={t("Forhåndsvisning og opplasting")}
          size="2xl"
          zIndex={3500}
        >
          {filesToUpload.length > 0 && (
            <>
              {/* File Previews */}
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {filesToUpload.map((file, index) => (
                  <div
                    key={index}
                    className="flex flex-col gap-3 py-4 bg-gray-100 rounded-xl px-4 relative"
                  >
                    <button
                      type="button"
                      onClick={() => handleDeleteFile(index)}
                      className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1.5"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                    <img
                      src={
                        file.file.type === "application/pdf"
                          ? PdfPlaceholder
                          : file.previewUrl
                      }
                      className="h-40 object-cover rounded"
                      alt="Preview"
                    />
                    <PMField
                      name="title"
                      type="text"
                      label={t("Tittel")}
                      value={file.title}
                      onChange={(e) => handleTitleChange(index, e.target.value)}
                    />
                  </div>
                ))}
              </div>

              {/* Progress Bar */}
              {isUploading && (
                <div className="mt-4 w-full bg-gray-300 rounded h-4">
                  <div
                    className="h-full bg-blue-500 rounded"
                    style={{
                      width: `${
                        (Object.values(uploadProgress).reduce((acc, val) => acc + val, 0) /
                          (filesToUpload.length * 100)) *
                        100
                      }%`,
                    }}
                  />
                </div>
              )}

              {/* Upload Status */}
              {isUploading && (
                <p className="text-center mt-2 text-sm text-gray-700">
                  {t("Opplasting")} {uploadedCount + 1} {t("av")} {filesToUpload.length}
                </p>
              )}

              {/* Upload Complete */}
              {uploadedCount === filesToUpload.length && isUploading && (
                <div className="flex justify-center items-center mt-4 text-green-500">
                  <CheckCircleIcon className="h-6 w-6" />
                  <span className="ml-2 text-green-600 font-semibold">{t("Opplasting fullført")}</span>
                </div>
              )}

              {/* Add More Files Button */}
              {!isUploading && (
                <button
                  type="button"
                  className="mt-4 flex items-center justify-center w-full bg-blue-500 text-white p-2 rounded hover:bg-blue-600 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <CameraIcon className="h-5 w-5 mr-2" /> {t("Legg til flere bilder")}
                </button>
              )}

              {/* Actions */}
              <div className="flex justify-between mt-4">
                <button
                  className="w-20 bg-gray-600 text-white p-2 rounded"
                  onClick={() => {
                    setUploadDialogOpen(false);
                    setFilesToUpload([]);
                  }}
                >
                  {t("Steng")}
                </button>
                <button
                  disabled={isUploading || !uploadFunctionRef.current}
                  className="w-20 p-2 rounded text-white bg-emerald-600 hover:bg-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  onClick={async () => {
                    if (!isUploading && typeof uploadFunctionRef.current === "function") {
                      try {
                        setIsUploading(true);
                        setUploadedCount(0);
                        setUploadProgress({});
                        await uploadFunctionRef.current();
                      } catch (error) {
                        console.error("Upload error:", error);
                        toast.error("Feil ved opplasting");
                        setIsUploading(false);
                      }
                    }
                  }}
                >
                  {isUploading ? t("Laster opp...") : t("Last opp")}
                </button>
              </div>
            </>
          )}
        </PMDialog>
      )}

      {/* Uploader Component */}
      <UploaderV4
        filesToUpload={filesToUpload}
        onUploadStart={(uploadFn) => {
          if (uploadFn && typeof uploadFn === "function") {
            uploadFunctionRef.current = uploadFn;
          }
        }}
        onUploadComplete={handleUploadComplete}
        onProgressChange={handleProgressChange}
        onFileUploadComplete={handleFileUploadComplete}
        onReset={setResetUploader}
      />

      {/* Edit Dialog */}
      {editDialogOpen && editingDocument && (
        <EditDocumentDialog
          open={editDialogOpen}
          onClose={() => {
            setEditDialogOpen(false);
            setEditingDocument(null);
          }}
          document={editingDocument}
          onRefresh={handleDocumentUpdated}
          editPublic={false}
        />
      )}

      {/* Image Viewer */}
      {viewerOpen && imageUrls.length > 0 && currentImageIndex < imageUrls.length && (
        <ImageViewer
          src={imageUrls}
          currentIndex={currentImageIndex}
          disableScroll={false}
          closeOnClickOutside={true}
          onClose={closeImageViewer}
          backgroundStyle={{
            backgroundColor: "rgba(0,0,0,0.9)",
          }}
        />
      )}
    </div>
  );
};

export default EnhancedLibrary;
